import {useState, useCallback} from 'react';
import {useSearchParams, useNavigate} from 'react-router-dom';

import {formatDateTimeWithMs} from '~/modules/utils';
import {useGetAiToolTracesQuery, useGetAiToolTraceNamesQuery} from '~/store/rtkQuery';

export const AiToolTracesScene = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL params
  const [page, setPage] = useState(() => {
    const pageParam = searchParams.get('page');
    return pageParam ? parseInt(pageParam, 10) : 0;
  });

  const [pageSize, setPageSize] = useState(() => {
    const pageSizeParam = searchParams.get('pageSize');
    return pageSizeParam ? parseInt(pageSizeParam, 10) : 20;
  });

  const [startDate, setStartDate] = useState(() => {
    return searchParams.get('startDate') || '';
  });

  const [endDate, setEndDate] = useState(() => {
    return searchParams.get('endDate') || '';
  });

  const [traceName, setTraceName] = useState(() => {
    return searchParams.get('traceName') || '';
  });

  const {data, isLoading, error} = useGetAiToolTracesQuery({
    page,
    pageSize,
    ...(startDate && {startDate}),
    ...(endDate && {endDate}),
    ...(traceName && {traceName}),
  });

  const {data: traceNamesData, isLoading: isLoadingTraceNames} =
    useGetAiToolTraceNamesQuery();

  // Helper function to update URL params
  const updateUrlParams = useCallback(
    (updates: {
      page?: number;
      pageSize?: number;
      startDate?: string;
      endDate?: string;
      traceName?: string;
    }) => {
      const newSearchParams = new URLSearchParams(searchParams);

      if (updates.page !== undefined) {
        if (updates.page === 0) {
          newSearchParams.delete('page');
        } else {
          newSearchParams.set('page', updates.page.toString());
        }
      }

      if (updates.pageSize !== undefined) {
        if (updates.pageSize === 20) {
          newSearchParams.delete('pageSize');
        } else {
          newSearchParams.set('pageSize', updates.pageSize.toString());
        }
      }

      if (updates.startDate !== undefined) {
        if (updates.startDate === '') {
          newSearchParams.delete('startDate');
        } else {
          newSearchParams.set('startDate', updates.startDate);
        }
      }

      if (updates.endDate !== undefined) {
        if (updates.endDate === '') {
          newSearchParams.delete('endDate');
        } else {
          newSearchParams.set('endDate', updates.endDate);
        }
      }

      if (updates.traceName !== undefined) {
        if (updates.traceName === '') {
          newSearchParams.delete('traceName');
        } else {
          newSearchParams.set('traceName', updates.traceName);
        }
      }

      setSearchParams(newSearchParams);
    },
    [searchParams, setSearchParams],
  );

  const onPageChange = useCallback(
    (newPage: number) => {
      setPage(newPage);
      updateUrlParams({page: newPage});
    },
    [updateUrlParams],
  );

  const onPageSizeChange = useCallback(
    (newPageSize: number) => {
      setPageSize(newPageSize);
      setPage(0);
      updateUrlParams({pageSize: newPageSize, page: 0});
    },
    [updateUrlParams],
  );

  const handleApplyFilters = useCallback(() => {
    setPage(0);
    updateUrlParams({
      page: 0,
      startDate,
      endDate,
      traceName,
    });
  }, [startDate, endDate, traceName, updateUrlParams]);

  const handleClearFilters = useCallback(() => {
    setStartDate('');
    setEndDate('');
    setTraceName('');
    setPage(0);
    updateUrlParams({
      page: 0,
      startDate: '',
      endDate: '',
      traceName: '',
    });
  }, [updateUrlParams]);

  const handleRowClick = useCallback(
    (traceId: string) => {
      navigate(`/ai-support/ai-tool-traces/${traceId}`);
    },
    [navigate],
  );

  if (error) {
    return (
      <div className="p-4">
        <div className="alert alert-error">
          <span>Error loading traces: {(error as any).message}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <h1 className="text-3xl font-bold mb-6">AI Tool Traces</h1>

      {/* Filters */}
      <div className="card bg-base-200 mb-4">
        <div className="card-body">
          <h2 className="card-title text-lg">Filters</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="form-control">
              <label className="label">
                <span className="label-text">Start Date</span>
              </label>
              <input
                type="date"
                className="input input-bordered"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
              />
            </div>
            <div className="form-control">
              <label className="label">
                <span className="label-text">End Date</span>
              </label>
              <input
                type="date"
                className="input input-bordered"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
              />
            </div>
            <div className="form-control">
              <label className="label">
                <span className="label-text">Trace Name</span>
              </label>
              <select
                className="select select-bordered"
                value={traceName}
                onChange={e => setTraceName(e.target.value)}
                disabled={isLoadingTraceNames}
              >
                <option value="">All Trace Names</option>
                {traceNamesData?.traceNames.map(name => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="card-actions justify-end mt-4">
            <button className="btn btn-ghost" onClick={handleClearFilters}>
              Clear
            </button>
            <button className="btn btn-primary" onClick={handleApplyFilters}>
              Apply Filters
            </button>
          </div>
        </div>
      </div>

      {/* Pagination Controls */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <span className="text-sm">Page Size:</span>
          <select
            className="select select-bordered select-sm"
            value={pageSize}
            onChange={e => onPageSizeChange(parseInt(e.target.value, 10))}
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
        <div className="text-sm">
          {data && (
            <span>
              Showing {page * pageSize + 1}-
              {Math.min((page + 1) * pageSize, data.totalCount)} of{' '}
              {data.totalCount} traces
            </span>
          )}
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center p-8">
          <span className="loading loading-dots loading-lg"></span>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="table table-zebra">
              <thead>
                <tr>
                  <th>Trace Name</th>
                  <th>Started At</th>
                  <th>Entries</th>
                  <th>Errors</th>
                  <th>Version</th>
                </tr>
              </thead>
              <tbody>
                {data?.traces.map(trace => (
                  <tr
                    key={trace.trace_id}
                    className="hover cursor-pointer"
                    onClick={() => handleRowClick(trace.trace_id)}
                  >
                    <td className="font-mono">{trace.trace_name}</td>
                    <td className="font-mono text-xs">
                      {formatDateTimeWithMs(trace.first_timestamp)}
                    </td>
                    <td>{trace.entry_count}</td>
                    <td>
                      {trace.error_count > 0 ? (
                        <span className="badge badge-error">
                          {trace.error_count}
                        </span>
                      ) : (
                        <span className="text-gray-500">0</span>
                      )}
                    </td>
                    <td>{trace.version || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex justify-center mt-4">
            <div className="join">
              <button
                className="join-item btn"
                disabled={page === 0}
                onClick={() => onPageChange(page - 1)}
              >
                Previous
              </button>
              <button className="join-item btn">Page {page + 1}</button>
              <button
                className="join-item btn"
                disabled={
                  !data || (page + 1) * pageSize >= data.totalCount
                }
                onClick={() => onPageChange(page + 1)}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
